import { isAxiosError } from "axios";
import type { FormStep5CustomerResponse, StepDataState } from "@/types/Form";
import type { MultiStepFormType, StepConfigs } from "@/utils/formConfig";
import { FormApi } from "@/api/FormApi";

const fetchOrNull = async <T>(fetcher: () => Promise<T>): Promise<T | null> => {
  try {
    return await fetcher();
  } catch (err) {
    if (isAxiosError(err) && err.response?.status === 404) return null;
    throw err;
  }
};

export interface HydrateFormDataResult {
  customer: FormStep5CustomerResponse | null;
  data: Partial<MultiStepFormType>;
  stepDataState: Record<keyof MultiStepFormType, StepDataState>;
}

export const hydrateFormDataFromApi = async (
  formId: string,
  stepConfigs: StepConfigs,
): Promise<HydrateFormDataResult> => {
  const formRoot = await fetchOrNull(() => FormApi.getOne(formId));

  const [step1, step2, step3, step4] = await Promise.all([
    fetchOrNull(() => FormApi.getStep1EnergyConsumption(formId)),
    fetchOrNull(() => FormApi.getStep2OperatorAndTariff(formId)),
    fetchOrNull(() => FormApi.getStep3EnergyBilling(formId)),
    fetchOrNull(() => FormApi.getStep4ConnectionAndInstallation(formId)),
  ]);

  return {
    customer: formRoot?.customer ?? null,
    data: {
      step1: step1 ? stepConfigs.step1.mapToForm(step1) : undefined,
      step2: step2 ? stepConfigs.step2.mapToForm(step2) : undefined,
      step3: step3 ? stepConfigs.step3.mapToForm(step3) : undefined,
      step4: step4 ? stepConfigs.step4.mapToForm(step4) : undefined,
      step5: formRoot?.customer ? stepConfigs.step5.mapToForm(formRoot.customer) : undefined,
      summary: { excelProfile: null },
    },
    stepDataState: {
      step1: step1 ? "sent" : "missing",
      step2: step2 ? "sent" : "missing",
      step3: step3 ? "sent" : "missing",
      step4: step4 ? "sent" : "missing",
      step5: formRoot?.customer ? "sent" : "missing",
      summary: "unknown",
    },
  };
};
